import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUserService, getPatientsService } from "../api/services";
import { PatientCard } from "../components/PatientCard";
import { SearchBar } from "../components/SearchBar";
import { UserAvatar } from "../components/UserAvatar";
import { PermissionsEnum, PlanStatusEnum } from "../constants/enums";
import { useRequestError } from "../hooks/useRequestError";
import type { UserModel } from "../types/models";
import { formatDisplayName } from "../utils/name";

const HOSPITAL_ASSIGNMENT_REQUIRED =
  "You are not assigned to an active hospital tenant";

export const HomePage = () => {
  const navigate = useNavigate();
  const { handleRequestError } = useRequestError();
  const [currentUser, setCurrentUser] = useState<UserModel | null>(null);
  const [activePatients, setActivePatients] = useState<UserModel[]>([]);
  const [pendingPatients, setPendingPatients] = useState<UserModel[]>([]);
  const [noActivePatients, setNoActivePatients] = useState<UserModel[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [planPickerQuery, setPlanPickerQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [requiresHospitalAssignment, setRequiresHospitalAssignment] =
    useState(false);

  const currentUserId = currentUser?.id;
  const isDoctor = Boolean(
    currentUser?.permissions?.includes(PermissionsEnum.DOCTOR)
  );
  const isCaregiver = Boolean(
    currentUser?.permissions?.includes(PermissionsEnum.CARE_GIVER)
  );

  useEffect(() => {
    let cancelled = false;
    getCurrentUserService()
      .then((user) => {
        if (!cancelled) {
          setCurrentUser(user);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          handleRequestError(error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [handleRequestError]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const loadPatients = useCallback(
    async (query: string) => {
      if (!currentUserId) {
        return;
      }
      setLoading(true);
      try {
        if (isDoctor) {
          const [active, pending] = await Promise.all([
            getPatientsService({
              limit: 50,
              offset: 0,
              my_patients: true,
              q: query || undefined,
            }),
            getPatientsService({
              limit: 50,
              offset: 0,
              pending_plan: true,
              q: query || undefined,
            }),
          ]);
          setRequiresHospitalAssignment(false);
          setActivePatients(active.result_list || []);
          setPendingPatients(pending.result_list || []);
        } else if (isCaregiver) {
          const [pending, active, noActive] = await Promise.all([
            getPatientsService({
              limit: 50,
              offset: 0,
              pending_plan: true,
              q: query || undefined,
            }),
            getPatientsService({
              limit: 50,
              offset: 0,
              my_patients: true,
              q: query || undefined,
            }),
            getPatientsService({
              limit: 50,
              offset: 0,
              no_active_plan: true,
              q: query || undefined,
            }),
          ]);
          setRequiresHospitalAssignment(false);
          setPendingPatients(pending.result_list || []);
          setActivePatients(active.result_list || []);
          setNoActivePatients(noActive.result_list || []);
        }
      } catch (error: unknown) {
        const detail = (
          error as { response?: { data?: { detail?: string }; status?: number } }
        )?.response?.data?.detail;
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403 && detail === HOSPITAL_ASSIGNMENT_REQUIRED) {
          setRequiresHospitalAssignment(true);
          setActivePatients([]);
          setPendingPatients([]);
          setNoActivePatients([]);
        } else {
          handleRequestError(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentUserId, handleRequestError, isCaregiver, isDoctor]
  );

  useEffect(() => {
    if (!currentUserId) {
      return;
    }
    loadPatients(debouncedQuery);
  }, [currentUserId, debouncedQuery, loadPatients]);

  const tabs = useMemo(() => {
    if (isDoctor) {
      return ["Active Patients", "Pending Patients"];
    }
    if (isCaregiver) {
      return ["Pending", "My Patients", "No Active Plan"];
    }
    return [];
  }, [isCaregiver, isDoctor]);

  const patients = useMemo(() => {
    if (isDoctor) {
      return selectedTab === 0 ? activePatients : pendingPatients;
    }
    if (selectedTab === 0) return pendingPatients;
    if (selectedTab === 1) return activePatients;
    return noActivePatients;
  }, [
    activePatients,
    isDoctor,
    noActivePatients,
    pendingPatients,
    selectedTab,
  ]);

  const planPickerPatients = useMemo(() => {
    const q = planPickerQuery.trim().toLowerCase();
    if (!q) {
      return noActivePatients;
    }
    return noActivePatients.filter((patient) => {
      const name = `${patient.first_name || ""} ${patient.last_name || ""}`
        .trim()
        .toLowerCase();
      return (
        name.includes(q) ||
        (patient.email || "").toLowerCase().includes(q) ||
        (patient.phone_number || "").includes(q)
      );
    });
  }, [noActivePatients, planPickerQuery]);

  const openCreateCarePlan = () => {
    setPlanPickerQuery("");
    setPlanPickerOpen(true);
    setSelectedTab(2);
  };

  const startPlanForPatient = (patientId: string) => {
    setPlanPickerOpen(false);
    navigate(`/plans/setup?user_id=${patientId}`);
  };

  const onPatientClick = (patient: UserModel) => {
    if (isDoctor) {
      if (patient.plans?.[0]?.id) {
        navigate(`/plans/${patient.plans[0].id}`);
      }
      return;
    }
    if (selectedTab === 0 && patient.plans?.[0]?.id) {
      navigate(`/plans/${patient.plans[0].id}`);
      return;
    }
    if (selectedTab === 2) {
      startPlanForPatient(patient.id);
      return;
    }
    navigate(`/patients/${patient.id}`);
  };

  const planStatusForTab = (): PlanStatusEnum => {
    if (isDoctor) {
      return selectedTab === 0 ? PlanStatusEnum.ACTIVE : PlanStatusEnum.PENDING;
    }
    if (selectedTab === 0) return PlanStatusEnum.PENDING;
    if (selectedTab === 1) return PlanStatusEnum.ACTIVE;
    return PlanStatusEnum.REJECTED;
  };

  const emptyMessage = (() => {
    if (isCaregiver && selectedTab === 2) {
      return "No patients without an active plan. Register a patient first, then create their care plan here.";
    }
    return "No patients found.";
  })();

  return (
    <div className="page">
      <header className="page-header">
        <div className="page-header-left">
          <UserAvatar
            uri={currentUser?.profile_image_url}
            name={formatDisplayName(
              currentUser?.first_name,
              currentUser?.last_name
            )}
            size={56}
          />
          <div>
            <div className="eyebrow">Good day</div>
            <h1>
              {currentUser
                ? formatDisplayName(
                    currentUser.first_name,
                    currentUser.last_name
                  )
                : "…"}
            </h1>
            <div className="muted">
              {isDoctor ? "Doctor" : isCaregiver ? "Care Giver" : "Staff"}
            </div>
          </div>
        </div>
        <div className="actions">
          {isCaregiver ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={openCreateCarePlan}
              disabled={requiresHospitalAssignment}
            >
              Create care plan
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/patients/register")}
            disabled={requiresHospitalAssignment}
          >
            Register patient
          </button>
        </div>
      </header>

      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search patients"
      />

      <div className="segmented tabs">
        {tabs.map((label, index) => (
          <button
            key={label}
            type="button"
            className={selectedTab === index ? "active" : ""}
            onClick={() => setSelectedTab(index)}
          >
            {label}
          </button>
        ))}
      </div>

      {isCaregiver && selectedTab === 2 && !requiresHospitalAssignment ? (
        <p className="muted">
          Tap a patient below to create a new care plan for them.
        </p>
      ) : null}

      {loading ? <div className="spinner" /> : null}

      {requiresHospitalAssignment ? (
        <div className="empty-state">
          Your hospital access is being set up. Patient records will be available
          once your hospital roster assignment is active.
        </div>
      ) : patients.length === 0 && !loading ? (
        <div className="empty-state">{emptyMessage}</div>
      ) : (
        <div className="card-list">
          {patients
            .filter((patient) => patient?.id)
            .map((patient) => (
              <PatientCard
                key={patient.id}
                user={patient}
                include_vitals={
                  isDoctor ? selectedTab === 0 : selectedTab === 1
                }
                plan_status={planStatusForTab()}
                onClick={() => onPatientClick(patient)}
                actionLabel={
                  isCaregiver && selectedTab === 2 ? "Create plan" : undefined
                }
              />
            ))}
        </div>
      )}

      {planPickerOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setPlanPickerOpen(false)}
        >
          <div
            className="modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-plan-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row-between">
              <h2 id="create-plan-title">Create care plan</h2>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPlanPickerOpen(false)}
              >
                Close
              </button>
            </div>
            <p className="muted">
              Choose a patient who does not have an active plan yet.
            </p>
            <SearchBar
              value={planPickerQuery}
              onChange={setPlanPickerQuery}
              placeholder="Search patients"
            />
            {planPickerPatients.length === 0 ? (
              <div className="empty-state">
                No eligible patients. Register a patient first.
              </div>
            ) : (
              <div className="card-list modal-list">
                {planPickerPatients.map((patient) => (
                  <PatientCard
                    key={patient.id}
                    user={patient}
                    plan_status={PlanStatusEnum.REJECTED}
                    actionLabel="Create plan"
                    onClick={() => startPlanForPatient(patient.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
